<template>
  <div>
    <div v-if="loading" class="text-center py-8 text-gray-300">Loading...</div>
    <div v-else-if="error" class="text-center py-8 text-red-400">{{ error }}</div>

    <template v-else-if="target">
      <section class="mb-4">
        <RouterLink :to="`/astronomers/${target.astronomer.slug}`">
          <h4 class="text-center mb-2">
            <img
              v-if="target.astronomer.avatar_url"
              :src="target.astronomer.avatar_url"
              class="rounded-full inline w-8 mr-1"
            />
            {{ target.astronomer.username }}
          </h4>
        </RouterLink>
        <h1 class="text-center">{{ target.name }}</h1>
        <div class="flex justify-center items-center">
          <div class="ml-4">
            <div class="statistics">
              <ul>
                <li>
                  {{ Number(target.statistics.session_count).toLocaleString() }}
                  {{ pluralize(Number(target.statistics.session_count), 'session', 'sessions') }}
                </li>
                <li>
                  {{ Number(target.statistics.image_count).toLocaleString() }}
                  {{ pluralize(Number(target.statistics.image_count), 'image', 'images') }}
                </li>
                <li>
                  {{ Number(target.statistics.image_duration).toLocaleString() }}
                  {{ pluralize(Number(target.statistics.image_duration), 'hour', 'hours') }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section v-if="target.image_url || target.details || target.can_edit" class="mb-4">
        <img v-if="target.image_url" :src="target.image_url" class="mb-8 mx-auto" />
        <template v-if="target.details">
          <h2>Details</h2>
          <!-- details is server-sanitized to a safe tag/attribute allowlist before serialization -->
          <div v-html="target.details" />
        </template>
        <a
          v-if="target.can_edit"
          :href="target.edit_url"
          class="text-yellow-300 text-sm underline mt-4 block"
        >
          Upload image and add details
        </a>
      </section>

      <section class="mb-16">
        <h2>Sessions</h2>
        <template v-if="filterTable">
          <table class="table-auto w-full divide-y-2 divide-gray-400 text-center">
            <thead>
              <tr>
                <th v-for="header in filterTable.headers" :key="header">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in filterTable.rows"
                :key="row[0]"
                :class="(i + 1) % 2 === 0 ? 'bg-gray-700' : 'bg-gray-800'"
              >
                <td v-for="(cell, j) in row" :key="j">{{ cell ?? '' }}</td>
              </tr>
              <tr>
                <td v-for="(cell, j) in filterTable.totalsRow" :key="j">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <p v-else class="text-gray-400">No sessions recorded yet.</p>
      </section>

      <section class="mt-16">
        <h2>Actions</h2>
        <div class="mt-2 flex flex-wrap gap-2 items-center">
          <a
            :href="target.nina_target_url"
            class="inline-block btn"
            title="Download the JSON file into your NINA Targets directory."
          >
            Export as N.I.N.A. Target
          </a>
          <a v-if="target.can_edit" :href="target.edit_url" class="inline-block btn">
            Edit target
          </a>
          <form
            v-if="target.can_edit"
            :action="target.delete_url"
            method="post"
            class="inline-block"
            @submit="confirmDelete"
          >
            <input type="hidden" name="_method" value="delete" />
            <input type="hidden" name="authenticity_token" :value="csrfToken" />
            <button type="submit" class="btn btn-warning">Delete target data</button>
          </form>
        </div>
        <div v-if="target.can_edit && target.merge_options && target.merge_options.length > 0" class="mt-2">
          <form :action="target.merge_url" method="post" class="flex flex-wrap gap-2 items-center" @submit="confirmMerge">
            <input type="hidden" name="_method" value="post" />
            <input type="hidden" name="authenticity_token" :value="csrfToken" />
            <select
              v-model="mergeIntoId"
              name="into_id"
              class="py-2 pl-3 pr-6 text-sm text-gray-700 border rounded-lg appearance-none focus:shadow-outline"
            >
              <option value="" disabled>Select a target to merge into…</option>
              <option v-for="opt in target.merge_options" :key="opt.id" :value="opt.id">
                {{ opt.label }}
              </option>
            </select>
            <button type="submit" class="btn">Merge data into selected target</button>
          </form>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { apiFetch } from '../utils/api'

const FILTER_SORT_ORDER = [
  'l', 'lum', 'luminance',
  'r', 'red',
  'g', 'green',
  'b', 'blue',
  'ha', 'h', 'h-a', 'h-alpha', 'hydrogen', 'hydrogen alpha', 'hydrogen-alpha',
  'oiii', 'oxygen', 'o3',
  'sii', 's', 's2', 'sulfur',
  'none',
]

interface FilterStat {
  date: string
  filter: string
  duration_hours: number
}

interface MergeOption {
  id: number
  label: string
}

interface Target {
  id: number
  name: string
  ra: number | null
  dec: number | null
  rotation: number | null
  image_url: string | null
  details: string | null
  can_edit: boolean
  astronomer: { username: string; avatar_url: string | null; slug: string }
  statistics: { session_count: number; image_count: number; image_duration: number }
  filter_stats: FilterStat[]
  nina_target_url: string
  edit_url: string
  merge_url: string | null
  merge_options: MergeOption[]
  delete_url: string | null
}

const route = useRoute()
const target = ref<Target | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const mergeIntoId = ref<number | ''>('')

const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

function confirmMerge(e: Event) {
  if (!confirm('Are you sure you want to merge these two targets? This cannot be undone!')) {
    e.preventDefault()
  }
}

function confirmDelete(e: Event) {
  if (!confirm('Are you sure? This will delete the target and all session & imaging information attached to it. This cannot be undone!')) {
    e.preventDefault()
  }
}

function pluralize(value: number, singular: string, plural: string): string {
  return value === 1 ? singular : plural
}

function filterSortIndex(filter: string): number {
  const idx = FILTER_SORT_ORDER.indexOf(filter.toLowerCase())
  return idx === -1 ? Infinity : idx
}

const filterTable = computed(() => {
  if (!target.value?.filter_stats?.length) return null

  const stats = target.value.filter_stats

  const uniqueFilters = [...new Set(stats.map((s) => s.filter))].sort(
    (a, b) => filterSortIndex(a) - filterSortIndex(b),
  )

  const headers = [
    'Date',
    ...uniqueFilters.map((f) => f.charAt(0).toUpperCase() + f.slice(1)),
  ]

  const dateMap = new Map<string, Map<string, number>>()
  for (const { date, filter, duration_hours } of stats) {
    if (!dateMap.has(date)) dateMap.set(date, new Map())
    dateMap.get(date)!.set(filter, duration_hours)
  }

  const columnTotals = new Array<number>(uniqueFilters.length).fill(0)
  const rows = [...dateMap.keys()].sort().map((date) => {
    const filterMap = dateMap.get(date)!
    const cells: (string | null)[] = [date]
    for (let i = 0; i < uniqueFilters.length; i++) {
      const val = filterMap.has(uniqueFilters[i]) ? filterMap.get(uniqueFilters[i])! : null
      cells.push(val !== null ? val.toFixed(2) : null)
      if (val !== null) columnTotals[i] += val
    }
    return cells
  })

  const totalsRow = [
    'Total',
    ...columnTotals.map((t) => (t > 0 ? t.toFixed(2) : '—')),
  ]

  return { headers, rows, totalsRow }
})

async function fetchTarget() {
  loading.value = true
  error.value = null
  try {
    target.value = await apiFetch(
      `/api/spa/astronomers/${route.params.slug}/targets/${route.params.id}`,
    ) as Target
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load target'
  } finally {
    loading.value = false
  }
}

onMounted(fetchTarget)
</script>
